import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SubuserI, SubuserPatchI, SubuserPermissionI, SubuserRole } from '../types/userSettings';

const roles: { id: string; localisation: string }[] = [
  {
    id: 'admin',
    localisation: 'users_setting_page.role_admin'
  },
  {
    id: 'user',
    localisation: 'users_setting_page.role_user'
  }
];

interface UserSettingsStateI {
  selectedUsers: SubuserI[];
  roles: { id: string; localisation: string }[];

  newUserFirstName: string;
  newUserSurname: string;
  newUserEmail: string;
  newUserRole: SubuserRole;

  newUserFirstNameValidation?: string;
  newUserSurnameValidation?: string;
  newUserEmailValidation?: string;

  selectedUser: SubuserI;
  selectedUserEdits?: SubuserPatchI;

  selectedUserFirstNameValidation?: string;
  selectedUserSurnameValidation?: string;

  selectedUserPermissions: SubuserPermissionI[];
  selectedUserTouchedPerms: boolean;
}

const initUsersState: UserSettingsStateI = {
  selectedUsers: [],
  roles,
  newUserFirstName: '',
  newUserSurname: '',
  newUserEmail: '',
  newUserRole: 'user',
  selectedUser: {
    username: '',
    name: '',
    surname: '',
    userRole: 'user'
  },
  selectedUserPermissions: [],
  selectedUserTouchedPerms: false
};

const userSettingsSlice = createSlice({
  name: 'userSettings',
  initialState: initUsersState,
  reducers: {
    setSelectedUsers: (state, action: PayloadAction<SubuserI[]>) => {
      state.selectedUsers = action.payload;
    },
    setNewUserFirstName: (state, action: PayloadAction<string>) => {
      state.newUserFirstName = action.payload;
    },
    setNewUserSurname: (state, action: PayloadAction<string>) => {
      state.newUserSurname = action.payload;
    },
    setNewUserEmail: (state, action: PayloadAction<string>) => {
      state.newUserEmail = action.payload;
    },
    setNewUserRole: (state, action: PayloadAction<SubuserRole>) => {
      state.newUserRole = action.payload;
    },
    setSelectedUser: (state, action: PayloadAction<SubuserI>) => {
      state.selectedUser = action.payload;
      state.selectedUserEdits = { username: action.payload.username };
      state.selectedUserPermissions = initUsersState.selectedUserPermissions;
      state.selectedUserTouchedPerms = initUsersState.selectedUserTouchedPerms;
      state.selectedUserFirstNameValidation = initUsersState.selectedUserFirstNameValidation;
      state.selectedUserSurnameValidation = initUsersState.selectedUserSurnameValidation;
    },
    setSelectedUserEdits: (state, action: PayloadAction<SubuserPatchI>) => {
      state.selectedUserEdits = action.payload;
    },
    setSelectedUserPermissions: (state, action: PayloadAction<SubuserPermissionI[]>) => {
      state.selectedUserPermissions = action.payload;
    },
    setSelectedUserTouchedPerms: (state) => {
      state.selectedUserTouchedPerms = true;
    },
    setNewUserFirstNameValidation: (state, action: PayloadAction<string | undefined>) => {
      state.newUserFirstNameValidation = action.payload;
    },
    setNewUserSurnameValidation: (state, action: PayloadAction<string | undefined>) => {
      state.newUserSurnameValidation = action.payload;
    },
    setNewUserEmailValidation: (state, action: PayloadAction<string | undefined>) => {
      state.newUserEmailValidation = action.payload;
    },
    setSelectedUserFirstNameValidation: (state, action: PayloadAction<string | undefined>) => {
      state.selectedUserFirstNameValidation = action.payload;
    },
    setSelectedUserSurnameValidation: (state, action: PayloadAction<string | undefined>) => {
      state.selectedUserSurnameValidation = action.payload;
    }
  }
});

export const {
  setSelectedUsers,
  setNewUserFirstName,
  setNewUserSurname,
  setNewUserEmail,
  setNewUserRole,
  setSelectedUser,
  setSelectedUserEdits,
  setSelectedUserPermissions,
  setSelectedUserTouchedPerms,
  setNewUserFirstNameValidation,
  setNewUserSurnameValidation,
  setNewUserEmailValidation,
  setSelectedUserFirstNameValidation,
  setSelectedUserSurnameValidation
} = userSettingsSlice.actions;

export default userSettingsSlice.reducer;
